import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowUpRight, ArrowDownRight, LayoutGrid, List, ChevronUp, ChevronDown, FileText,
  DollarSign, TrendingUp, Activity, Users, Truck, Heart, BarChart3, Factory, Wallet,
} from 'lucide-react';
import { SET_REPORT_DATA } from '../../store/actions';
import axiosInstance from '../../axiosInstance';
import configData from '../../config';
import { PageHeader } from './PageHeader';
import { AICommentaryPanel } from './AICommentaryPanel';
import { cn } from '../lib/utils';
import { getUserAccountId, getUserRole } from '../lib/access';

const api = (path) => `${String(configData.API_SERVER || '').replace(/\/+$/, '')}${path}`;

// ─── POWER BI URL HELPERS (module scope — no closure issues) ─────────────────
// Derive isSecure / pbReportId / groupId from the embedURL shape:
//   /reportEmbed?reportId=...&groupId=...  →  secure (SDK embed, needs token)
//   /view?r=...                            →  public (plain iframe)
function parsePowerBIUrl(url) {
  if (!url) return { isSecure: false, pbReportId: null, groupId: null };
  try {
    const u = new URL(url);
    if (u.pathname.includes('/reportEmbed')) {
      return { isSecure: true, pbReportId: u.searchParams.get('reportId'), groupId: u.searchParams.get('groupId') };
    }
  } catch (_) { /* not a valid URL — treat as public */ }
  return { isSecure: false, pbReportId: null, groupId: null };
}

// Walk report tree and attach derived fields to every leaf node.
// Handles both camelCase (isSecure) and snake_case (is_secure) from DRF serializers.
// DB fields take priority; URL-parsed values are the fallback.
function augmentReports(items) {
  return (items || []).map(r => {
    if (r.children?.length) return { ...r, children: augmentReports(r.children) };
    const { isSecure: urlSecure, pbReportId: urlPbId, groupId: urlGroupId } = parsePowerBIUrl(r.embedURL);
    const dbSecure   = r.isSecure   != null ? r.isSecure   : r.is_secure;
    const dbGroupId  = r.groupId    != null ? r.groupId    : r.group_id;
    const dbDataset  = r.datasetId  != null ? r.datasetId  : r.dataset_id;
    const dbRlsRole  = r.rlsRole    != null ? r.rlsRole    : r.rls_role;
    const dbPbId     = r.reportId   != null ? r.reportId   : r.report_id;
    return {
      ...r,
      isSecure:   dbSecure  != null ? Boolean(dbSecure)  : urlSecure,
      pbReportId: dbPbId    || urlPbId    || null,
      groupId:    dbGroupId || urlGroupId || null,
      datasetId:  dbDataset  || null,
      rlsRole:    dbRlsRole  || null,
    };
  });
}

// ─── KPI DISPLAY METADATA — DERIVED FOR DISPLAY ONLY, NEVER FOR VALUES ───────
// kpi_master (DB) stores kpi_code / kpi_card_name / department / formula_code.
// Everything visual (icon, color, format, "which direction is good") is inferred
// from those fields below, so adding a new row to kpi_master is enough — no
// frontend deploy needed for a new KPI to show up correctly.
//
// IMPORTANT: none of this touches KPI *values*. All value/prev numbers come
// straight from the backend's `metrics` payload (see buildActivityKpiData
// below) — the frontend does not calculate, sum, or derive any KPI value.

const KPI_COLOR_PALETTE = [
  '#3b82f6', '#0ea5e9', '#10b981', '#06b6d4', '#8b5cf6', '#ec4899',
  '#f59e0b', '#14b8a6', '#64748b', '#ef4444', '#f97316', '#84cc16', '#a855f7',
];

// Deterministic hash → same kpi_code always gets the same color, without storing one.
function hashColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return KPI_COLOR_PALETTE[Math.abs(hash) % KPI_COLOR_PALETTE.length];
}

const DEPT_ICON = {
  finance: DollarSign,
  sales: TrendingUp,
  operations: Activity,
  hr: Users,
  'human-capital': Users,
  supply: Truck,
  'supply-chain': Truck,
  customer: Heart,
  production: Factory,
  overview: LayoutGrid,
  cost: Factory,
  cashflow: Wallet,
};

function inferIcon(department = '') {
  const key = String(department || '').toLowerCase().trim().replace(/[_\s]+/g, '-');
  return DEPT_ICON[key] || BarChart3;
}

// Percent-shaped KPIs (rate, margin, %, retention, delivery, etc.)
const PERCENT_HINT = /pct|percent|%|margin|\brate\b|market share|forecast variance|otif|on[- ]?time|retention|churn|attrition|utilisation|utilization/i;
// Plain-count KPIs (quantity, headcount, accounts, requests, scores that aren't money)
const NUMBER_HINT = /qty|quantity|count|headcount|units?\b|\breqs?\b|open positions?|vacanc|deals?\b|accounts?|nps|csat|score|index|orders?|tickets?/i;
// Ratio-out-of-5 style KPIs
const RATIO_HINT = /satisfaction|dealer score|rating|\bcsi\b|\bssi\b/i;
// Duration KPIs. "per day" is a frequency (for example repair orders/day),
// not a duration and must remain a number.
const DAYS_HINT = /\bdays?\b|time to hire|lead time|cycle time|turnaround|\btat\b/i;
const CURRENCY_HINT = /cash|capital|capex|value|price|cost|demurrage|freight|revenue|profit|sales value|order book/i;

function inferFormat(name = '', formulaCode = '') {
  const formula = String(formulaCode || '').toUpperCase();
  if (formula.includes('AED')) return 'aed';
  // The KPI name is the strongest signal for monetary cards. This must run
  // before generic formula markers because a cash-flow KPI must never render
  // as a score such as "196500000.0 / 5" when its stored formula metadata is
  // broad or stale.
  if (CURRENCY_HINT.test(name)) return 'aed';
  if (formula.includes('DAYS')) return 'days';
  if (formula.includes('PERCENT')) return 'percent';
  if (formula.includes('RATIO')) return 'ratio';
  if (formula.includes('NUMBER')) return 'number';
  if (RATIO_HINT.test(name)) return 'ratio';
  if (PERCENT_HINT.test(name)) return 'percent';
  if (DAYS_HINT.test(name) && !/\bper day\b|\/\s*day\b/i.test(name)) return 'days';
  if (NUMBER_HINT.test(name)) return 'number';
  return 'currency';
}

// KPIs where a lower number is the desired direction (costs, losses, churn, etc.)
const DOWN_IS_GOOD_HINT = /cost|loss|attrition|churn|receivable|purchase|salary|expense|\bopen\b|defect|downtime|complaint|delay|wastage|rejection/i;

function inferDirection(name = '') {
  return DOWN_IS_GOOD_HINT.test(name) ? 'down' : 'up';
}

function prettifyDept(dept) {
  if (!dept) return 'General';
  return String(dept).replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function normalizeDepartment(dept = '') {
  const normalized = String(dept || '').toLowerCase().trim().replace(/[_\s]+/g, '-');
  const aliases = {
    financial: 'finance',
    operation: 'operations',
    ops: 'operations',
    aftersales: 'operations',
    'aftersales-pdi': 'operations',
    hr: 'human-capital',
    people: 'human-capital',
    'human-resources': 'human-capital',
    supply: 'supply-chain',
    procurement: 'supply-chain',
    customers: 'customer',
    'customer-experience': 'customer',
    cx: 'customer',
  };
  return aliases[normalized] || normalized;
}

function titleCaseFromCode(code = '') {
  return String(code).toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Builds display metadata (name/category/icon/color/format/direction) for every
// KPI code present in the merged metrics map. `kpiData` is the backend-sourced
// metrics object (see loadKpis) where kpi_master-backed codes carry
// kpi_card_name / department / formula_code alongside value / prev.
function buildKpiDefs(kpiData = {}) {
  const defs = {};
  Object.entries(kpiData).forEach(([code, entry]) => {
    if (!code) return;
    const department = entry?.department || null;
    const name = entry?.kpi_card_name || entry?.kpiCardName || titleCaseFromCode(code);
    defs[code] = {
      code,
      formulaCode: entry?.formula_code || entry?.formulaCode || null,
      name,
      category: prettifyDept(department),
      icon: inferIcon(department),
      color: hashColor(code),
      format: inferFormat(name, entry?.formula_code || entry?.formulaCode),
      good: inferDirection(name),
    };
  });
  return defs;
}

const fmt = (value, format) => {
  if (value == null) return '-';
  if (format === 'aed') {
    if (Math.abs(value) >= 1000000000) return `AED ${(value / 1000000000).toFixed(2)}B`;
    if (Math.abs(value) >= 1000000) return `AED ${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `AED ${(value / 1000).toFixed(1)}K`;
    return `AED ${value.toLocaleString()}`;
  }
  if (format === 'days') return `${value.toLocaleString()} days`;
  if (format === 'currency') {
    if (value >= 10000000) return `Rs.${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000)   return `Rs.${(value / 100000).toFixed(1)}L`;
    if (value >= 1000)     return `Rs.${(value / 1000).toFixed(1)}K`;
    return `Rs.${value.toLocaleString()}`;
  }
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'ratio')   return `${value.toFixed(1)} / 5`;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000)    return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// pct() only compares two already-backend-provided numbers for display
// (e.g. the "X% vs prev" badge) — it does not derive KPI values themselves.
const pct = (curr, prev) => (!prev ? 0 : ((curr - prev) / prev) * 100);

const toNumber = (value) => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed != null) return parsed;
  }
  return null;
};

// Normalizes one backend metric entry into { value, prev }. This only type-casts
// / picks between already-computed backend fields (value/current/total/etc,
// prev/previous/prior/etc) — it never sums, subtracts, or derives a new number.
const normalizeMetricEntry = (entry) => {
  if (entry == null) return null;
  if (typeof entry === 'number' || typeof entry === 'string') {
    const value = toNumber(entry);
    return value == null ? null : { value, prev: null };
  }
  const value = firstNumber(entry.value, entry.current, entry.current_value, entry.total, entry.amount, entry.actual);
  const prev = firstNumber(entry.prev, entry.previous, entry.previous_value, entry.prior, entry.prior_value);
  if (value == null && prev == null) return null;
  return { value: value ?? null, prev: prev ?? null };
};

// ─── KPI DATA — PASS-THROUGH FROM BACKEND ONLY ───────────────────────────────
// activity_dashboard_metrics() already computes every KPI value server-side
// (revenue, margins, headcount, etc.) and returns it in `payload.metrics`.
// This function ONLY normalizes/typecasts that response for the UI — it does
// NOT sum raw activity rows, filter by keyword, or re-derive any value.
// All calculation lives in the backend now.
function buildActivityKpiData(payload = {}) {
  const result = {};
  const directMetrics = payload.metrics || {};

  Object.entries(directMetrics).forEach(([key, entry]) => {
    const normalized = normalizeMetricEntry(entry);
    if (!normalized) return;

    result[String(key).toUpperCase()] = {
      ...normalized,
      // carry through kpi_master metadata (formula_code / kpi_card_name /
      // department) so buildKpiDefs downstream can derive icon/color/
      // format/direction for DISPLAY purposes only.
      formula_code: entry?.formula_code || entry?.formulaCode || null,
      kpi_card_name: entry?.kpi_card_name || entry?.kpiCardName || null,
      department: entry?.department || null,
    };
  });

  return result;
}

function calcKPI(kpiCode, dataMap, kpiDefs) {
  const kpi = kpiDefs?.[kpiCode];
  if (!kpi) return null;
  const d         = dataMap?.[kpiCode] || {};
  const value     = d.value ?? null;
  const prev      = d.prev ?? null;
  const hasValue  = value != null;
  const changePct = hasValue ? pct(value, prev) : null;
  const changeAbs = value != null && prev != null ? value - prev : null;
  const isGood    = hasValue && (kpi.good === 'up' ? changePct >= 0 : changePct <= 0);
  let status;
  if (!hasValue) {
    status = 'No Data';
  } else if (isGood) {
    status = Math.abs(changePct) >= 5 ? 'Strong' : 'On Track';
  } else {
    status = Math.abs(changePct) >= 10 ? 'Critical' : 'Needs Attention';
  }
  return { kpi, value, prev, changePct, changeAbs, isGood, status };
}

const normalizeKpiCard = (card = {}) => {
  const kpiCode = card.kpiCode || card.kpi_code || card.code || card.kpi || null;
  return {
    ...card,
    kpiCode,
    formulaCode: card.formulaCode || card.formula_code || null,
    reportId: card.reportId || card.report_id || null,
    reportTitle: card.reportTitle || card.report_title || card.title || card.name || null,
    datasetId: card.datasetId || card.dataset_id || null,
    groupId: card.groupId || card.group_id || null,
    pbReportId: card.pbReportId || card.pb_report_id || null,
    isSecure: card.isSecure ?? card.is_secure ?? false,
    rlsRole: card.rlsRole || card.rls_role || null,
    embedUrl: card.embedUrl || card.embed_url || card.embedURL || null,
  };
};

const cardsFromKpiMaster = (rows = []) => rows
  .map((row) => {
    const kpiCode = row.kpi_code || row.kpiCode || null;
    if (!kpiCode) return null;
    return normalizeKpiCard({
      kpiCode,
      formulaCode: row.formula_code || row.formulaCode || null,
      srNo: row.sr_no || row.srNo || 999,
    });
  })
  .filter(Boolean)
  .sort((a, b) => (a.srNo || 999) - (b.srNo || 999));

// ─── KPI CARD — same look & feel as overview KpiCard ─────────────────────────
const KPICard = ({ card, period, kpiData, kpiDefs, onClick, onRemove, editMode, isDragging }) => {
  const normalizedCard = normalizeKpiCard(card);
  const kpi = kpiDefs?.[normalizedCard.kpiCode];
  if (!kpi) return null;
  const data      = kpiData?.[kpi.code] || { value: null, prev: null };
  const hasValue  = data.value != null;
  const change    = hasValue ? pct(data.value, data.prev) : null;
  const isGood    = hasValue && (kpi.good === 'up' ? change >= 0 : change <= 0);
  const hasReport = !!normalizedCard.reportId;
  const KpiIcon = kpi.icon || BarChart3;

  return (
    <div
      onClick={() => hasReport && onClick()}
      className={cn(
        'group surface-card relative overflow-hidden p-4 sm:p-5 transition-transform select-none',
        hasReport && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-lg',
        isDragging && 'shadow-2xl rotate-1 scale-105',
      )}
    >
      {editMode && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onRemove(); }}
          className="absolute top-2 right-2 z-30 flex items-center justify-center rounded-full border font-bold leading-none shadow-sm transition"
          style={{
            width: 24,
            height: 24,
            background: '#fff',
            borderColor: 'rgba(217,79,58,0.35)',
            color: '#d94f3a',
            fontSize: 14,
          }}
          title="Remove card"
          aria-label={`Remove ${kpi.name}`}
        >
          x
        </button>
      )}
      {editMode && (
        <span className="absolute top-2 left-2 z-10 text-muted-foreground/40 cursor-grab text-base leading-none select-none">⠇</span>
      )}

      <div className="relative flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium truncate">
            {kpi.name}
          </p>
          <p
            className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight break-words"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            {fmt(data.value, kpi.format)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{kpi.category} · {period}</p>
        </div>
        <div
          className="h-10 w-10 grid place-items-center rounded-lg border flex-shrink-0 transition-colors"
          style={{ background: `${kpi.color}15`, borderColor: `${kpi.color}30`, color: kpi.color }}
        >
          <KpiIcon className="h-4 w-4" />
        </div>
      </div>

      <div className="relative mt-4 flex flex-wrap items-center justify-between gap-2">
        <span
          className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
          style={{
            borderColor: !hasValue ? 'rgba(100,116,139,0.25)' : isGood ? 'rgba(30,138,74,0.3)' : 'rgba(217,79,58,0.3)',
            background:  !hasValue ? 'rgba(100,116,139,0.08)' : isGood ? 'rgba(30,138,74,0.1)' : 'rgba(217,79,58,0.1)',
            color:       !hasValue ? 'var(--color-muted-foreground, #64748b)' : isGood ? 'var(--color-success, #1e8a4a)' : 'var(--color-destructive, #d94f3a)',
          }}
        >
          {hasValue && (isGood
            ? <ArrowUpRight style={{ width: 12, height: 12 }} />
            : <ArrowDownRight style={{ width: 12, height: 12 }} />)}
          {hasValue ? `${Math.abs(change).toFixed(1)}% vs prev` : '-'}
        </span>
        <span className="text-[11px] text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0">
          {hasReport ? 'Open report →' : editMode ? 'Link report' : '–'}
        </span>
      </div>

      {hasReport && (
        <p className="mt-2 text-[10px] text-muted-foreground/60 truncate">
          📋 {card.reportTitle}
        </p>
      )}
    </div>
  );
};

// ─── ADD KPI MODAL ─────────────────────────────────────────────────────────────
const AddKPIModal = ({ activeKpiCodes, availableKpis, reports, onAdd, onClose }) => {
  const [step,           setStep]           = useState(1);
  const [kpiSearch,      setKpiSearch]      = useState('');
  const [cat,            setCat]            = useState('All');
  const [selectedKpi,    setSelectedKpi]    = useState(null);
  const [reportSearch,   setReportSearch]   = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  const cats = ['All', ...Array.from(new Set(availableKpis.map(k => k.category)))];
  const shown = availableKpis.filter(k =>
    !activeKpiCodes.includes(k.code) &&
    (cat === 'All' || k.category === cat) &&
    k.name.toLowerCase().includes(kpiSearch.toLowerCase())
  );
  const flattenLeafReports = (items) => {
    const out = [];
    (items || []).forEach(r => {
      if (r.children && r.children.length > 0) {
        out.push(...flattenLeafReports(r.children));
      } else if (r.type !== 'collapse') {
        out.push(r);
      }
    });
    return out;
  };
  const flatReports = flattenLeafReports(reports)
    .filter(r => r.title || r.name)
    .filter(r => (r.title || r.name || '').toLowerCase().includes(reportSearch.toLowerCase()));

  const confirm = () => {
    onAdd({
      kpiCode:     selectedKpi.code,
      formulaCode: selectedKpi.formulaCode,
      reportId:    selectedReport ? (selectedReport.id || selectedReport.reportId || null) : null,
      reportTitle: selectedReport ? (selectedReport.title || selectedReport.name || null) : null,
      datasetId:   selectedReport?.datasetId  || null,
      groupId:     selectedReport?.groupId    || null,
      pbReportId:  selectedReport?.pbReportId || null,
      isSecure:    selectedReport?.isSecure   || false,
      rlsRole:     selectedReport?.rlsRole    || null,
      embedUrl:    selectedReport?.embedURL   || selectedReport?.embedUrl || selectedReport?.embed_url || null,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.65)' }} onClick={onClose}>
      <div className="surface-card rounded-2xl w-full max-w-md max-h-[88vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>

        <div className="p-5 pb-3 border-b border-border/60">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-semibold" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              {step === 1 ? 'Choose a KPI' : 'Link a Report'}
            </h3>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-sm">✕</button>
          </div>
          <div className="flex items-center gap-2">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold', step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                  {s}
                </div>
                {s < 2 && <div className={cn('flex-1 h-0.5 rounded', step > s ? 'bg-primary' : 'bg-border')} />}
              </React.Fragment>
            ))}
            <span className="text-[11px] text-muted-foreground ml-1">{step === 1 ? 'Select metric' : 'Select report (optional)'}</span>
          </div>
        </div>

        {step === 1 && <>
          <div className="px-5 pt-3 pb-2 border-b border-border/40">
            <input
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm outline-none focus:border-primary transition-colors"
              placeholder="Search KPIs..."
              value={kpiSearch}
              onChange={e => setKpiSearch(e.target.value)}
            />
            <div className="flex gap-1.5 flex-wrap mt-2">
              {cats.map(c => (
                <button key={c} onClick={() => setCat(c)} className={cn('px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors', cat === c ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50')}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto p-4 flex flex-col gap-1.5">
            {shown.length === 0 && (
              <div className="text-center py-8 px-4">
                <p className="text-sm font-medium text-foreground">
                  {availableKpis.length === 0 ? 'No KPI cards are allocated to you' : 'All available KPI cards are already on this dashboard'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {availableKpis.length === 0
                    ? 'Ask the Owner or Super Admin to assign cards from User Dashboard Allocation.'
                    : 'Remove a card first if you want to add it again with a different report.'}
                </p>
              </div>
            )}
            {shown.map(kpi => (
              <button key={kpi.code}
                onClick={() => { setSelectedKpi(kpi); setStep(2); }}
                className="flex items-center gap-3 p-3 rounded-xl border border-border/60 hover:border-primary/40 hover:bg-primary/5 transition text-left group"
              >
                <div className="h-9 w-9 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${kpi.color}15`, color: kpi.color }}>
                  {React.createElement(kpi.icon || BarChart3, { className: 'h-4 w-4' })}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{kpi.name}</p>
                  <p className="text-[11px] text-muted-foreground">{kpi.category}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            ))}
          </div>
        </>}

        {step === 2 && selectedKpi && <>
          <div className="px-5 py-2.5 bg-muted/30 border-b border-border/40 flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg grid place-items-center flex-shrink-0" style={{ background: `${selectedKpi.color}15`, color: selectedKpi.color }}>
              {React.createElement(selectedKpi.icon || BarChart3, { className: 'h-3.5 w-3.5' })}
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-muted-foreground">KPI Selected</p>
              <p className="text-sm font-semibold">{selectedKpi.name}</p>
            </div>
            <button onClick={() => { setStep(1); setSelectedReport(null); }} className="text-[11px] text-primary font-semibold hover:underline">Back</button>
          </div>
          <div className="px-5 pt-3 pb-2 border-b border-border/40">
            <input
              autoFocus
              className="w-full px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm outline-none focus:border-primary transition-colors"
              placeholder="Search reports..."
              value={reportSearch}
              onChange={e => setReportSearch(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground mt-1.5">Which Power BI report opens when this card is clicked?</p>
          </div>
          <div className="overflow-y-auto p-4 flex flex-col gap-1.5">
            {[null, ...flatReports].map((r, i) => {
              const isNone = r === null;
              const id     = isNone ? '__none__' : (r.reportId || r.id);
              const title  = isNone ? 'KPI only — no report' : (r.title || r.name);
              const isSel  = isNone ? selectedReport === null : (selectedReport?.reportId === id || selectedReport?.id === id);
              return (
                <button key={id || i}
                  onClick={() => setSelectedReport(isNone ? null : r)}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-xl border transition text-left',
                    isSel ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-primary/40 hover:bg-primary/5'
                  )}
                >
                  <div className={cn('h-9 w-9 rounded-lg grid place-items-center text-base flex-shrink-0', isNone ? 'bg-muted' : r?.isSecure ? 'bg-amber-50' : 'bg-emerald-50')}>
                    {isNone ? '⏭' : r?.isSecure ? '🔒' : '📊'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{title}</p>
                    <p className="text-[11px] text-muted-foreground">{isNone ? 'Card shows value only' : r?.isSecure ? 'Secure · RLS' : 'Public · Power BI'}</p>
                  </div>
                  {isSel && <span className="text-primary text-sm font-bold flex-shrink-0">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="p-4 border-t border-border/60">
            <button onClick={confirm} className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition">
              Add "{selectedKpi.name}" to Dashboard
            </button>
          </div>
        </>}
      </div>
    </div>
  );
};

// ─── PINNED REPORT CARD ────────────────────────────────────────────────────────
const STATUS_STYLE = {
  'No Data': { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.25)', color: '#64748b' },
  Strong: { bg: 'rgba(30,138,74,0.12)', border: 'rgba(30,138,74,0.3)', color: '#1e8a4a' },
  'On Track': { bg: 'rgba(30,138,74,0.07)', border: 'rgba(30,138,74,0.2)', color: '#2a9d5c' },
  'Needs Attention': { bg: 'rgba(217,165,30,0.10)', border: 'rgba(217,165,30,0.3)', color: '#b07d0b' },
  Critical: { bg: 'rgba(217,79,58,0.10)', border: 'rgba(217,79,58,0.3)', color: '#d94f3a' },
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ChevronUp style={{ width: 11, height: 11, opacity: 0.25 }} />;
  return sortDir === 'asc'
    ? <ChevronUp style={{ width: 11, height: 11, color: 'var(--color-primary)' }} />
    : <ChevronDown style={{ width: 11, height: 11, color: 'var(--color-primary)' }} />;
};

const TH = ({ col, label, sortBy, sortDir, onSort, right }) => (
  <th
    onClick={() => onSort(col)}
    className={cn(
      'px-4 py-3 text-[11px] uppercase tracking-[0.14em] font-semibold cursor-pointer select-none whitespace-nowrap',
      right ? 'text-right' : 'text-left',
    )}
    style={{ color: sortBy === col ? 'var(--color-foreground)' : 'var(--color-muted-foreground)' }}
  >
    <span className="inline-flex items-center gap-1">
      {label} <SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
    </span>
  </th>
);

const KPITable = ({ cards, period, kpiData, kpiDefs, onCardClick }) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [catFilter, setCatFilter] = useState('All');

  const rows = cards.map(rawCard => {
    const card = normalizeKpiCard(rawCard);
    const calc = calcKPI(card.kpiCode, kpiData, kpiDefs);
    if (!calc) return null;
    return { ...calc, card };
  }).filter(Boolean);

  const cats = ['All', ...Array.from(new Set(rows.map(r => r.kpi.category)))];
  const visible = rows.filter(r => catFilter === 'All' || r.kpi.category === catFilter);

  const sorted = [...visible].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortBy === 'name') return dir * a.kpi.name.localeCompare(b.kpi.name);
    if (sortBy === 'category') return dir * a.kpi.category.localeCompare(b.kpi.category);
    if (sortBy === 'value') return dir * ((a.value || 0) - (b.value || 0));
    if (sortBy === 'prev') return dir * ((a.prev || 0) - (b.prev || 0));
    if (sortBy === 'changePct') return dir * ((a.changePct ?? 0) - (b.changePct ?? 0));
    if (sortBy === 'changeAbs') return dir * ((a.changeAbs || 0) - (b.changeAbs || 0));
    if (sortBy === 'status') return dir * a.status.localeCompare(b.status);
    return 0;
  });

  const toggleSort = col => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const thProps = col => ({ col, sortBy, sortDir, onSort: toggleSort });

  return (
    <div className="surface-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-border/60 flex-wrap">
        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wide mr-1">Filter:</span>
        {cats.map(c => (
          <button
            key={c}
            onClick={() => setCatFilter(c)}
            className={cn(
              'px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors',
              catFilter === c
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
            )}
          >
            {c}
          </button>
        ))}
        <span className="sm:ml-auto text-[11px] text-muted-foreground">{sorted.length} KPI{sorted.length !== 1 ? 's' : ''} - {period}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border/60 bg-muted/30">
              <TH col="name" label="KPI" {...thProps('name')} />
              <TH col="category" label="Category" {...thProps('category')} />
              <TH col="value" label="Current" {...thProps('value')} right />
              <TH col="prev" label="Prior Period" {...thProps('prev')} right />
              <TH col="changeAbs" label="Delta Absolute" {...thProps('changeAbs')} right />
              <TH col="changePct" label="Delta %" {...thProps('changePct')} right />
              <TH col="status" label="Status" {...thProps('status')} />
              <th className="px-4 py-3 text-[11px] uppercase tracking-[0.14em] font-semibold text-left text-muted-foreground">Report</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {sorted.map(({ kpi, value, prev, changePct, changeAbs, isGood, status, card }) => {
              const ss = STATUS_STYLE[status] || STATUS_STYLE['On Track'];
              return (
                <tr key={kpi.code} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <span style={{ width: 30, height: 30, borderRadius: 8, background: `${kpi.color}18`, color: kpi.color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        {React.createElement(kpi.icon || BarChart3, { className: 'h-3.5 w-3.5' })}
                      </span>
                      <span className="text-sm font-medium whitespace-nowrap">{kpi.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{kpi.category}</td>
                  <td className="px-4 py-3 text-right"><span className="text-sm font-mono font-semibold">{fmt(value, kpi.format)}</span></td>
                  <td className="px-4 py-3 text-right"><span className="text-sm font-mono text-muted-foreground">{fmt(prev, kpi.format)}</span></td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-mono font-medium" style={{ color: isGood ? '#1e8a4a' : '#d94f3a' }}>
                      {changeAbs != null ? (changeAbs >= 0 ? '+' : '') + fmt(changeAbs, kpi.format) : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className="inline-flex items-center justify-end gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                      style={{ background: isGood ? 'rgba(30,138,74,0.1)' : 'rgba(217,79,58,0.1)', borderColor: isGood ? 'rgba(30,138,74,0.3)' : 'rgba(217,79,58,0.3)', color: isGood ? '#1e8a4a' : '#d94f3a' }}
                    >
                      {changePct != null && (isGood ? <ArrowUpRight style={{ width: 10, height: 10 }} /> : <ArrowDownRight style={{ width: 10, height: 10 }} />)}
                      {changePct != null ? `${Math.abs(changePct).toFixed(1)}%` : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex text-[11px] font-semibold px-2.5 py-0.5 rounded-full border whitespace-nowrap" style={{ background: ss.bg, borderColor: ss.border, color: ss.color }}>
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {card.reportId ? (
                      <button onClick={() => onCardClick(card)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:underline">
                        <FileText style={{ width: 12, height: 12 }} />
                        {card.reportTitle || 'Open'}
                      </button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/50">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {sorted.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-border bg-muted/40">
                <td className="px-4 py-2.5 text-[11px] font-semibold text-muted-foreground" colSpan={2}>
                  {sorted.filter(r => r.isGood).length} of {sorted.length} KPIs on track
                </td>
                <td colSpan={6} className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-3 flex-wrap">
                    {['Strong', 'On Track', 'Needs Attention', 'Critical'].map(s => {
                      const count = sorted.filter(r => r.status === s).length;
                      if (!count) return null;
                      const ss2 = STATUS_STYLE[s];
                      return (
                        <span key={s} className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: ss2.color }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: ss2.color, display: 'inline-block' }} />
                          {count} {s}
                        </span>
                      );
                    })}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export const PinnedReportCard = ({ myReport, onOpen, onConfigure, editMode }) => {
  const hasReport = !!myReport.reportId;
  return (
    <div
      onClick={() => hasReport && onOpen()}
      className={cn(
        'ih-pinned-report-card group relative overflow-hidden p-4 sm:p-5 rounded-[inherit] transition',
        hasReport ? 'cursor-pointer' : '',
      )}
      style={{
        background: hasReport
          ? 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)'
          : 'var(--color-muted, #f8fafc)',
        border: hasReport ? 'none' : '2px dashed var(--color-border, #cbd5e1)',
        borderRadius: 'var(--radius, 0.75rem)',
        minHeight: 148,
        boxShadow: hasReport ? '0 4px 20px rgba(15,23,42,0.25)' : 'none',
      }}
      onMouseEnter={e => { if (hasReport) e.currentTarget.style.boxShadow = '0 8px 32px rgba(15,23,42,0.35)'; }}
      onMouseLeave={e => { if (hasReport) e.currentTarget.style.boxShadow = '0 4px 20px rgba(15,23,42,0.25)'; }}
    >
      {hasReport && <>
        <div style={{ position:'absolute', width:140, height:140, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.06)', top:-40, right:-40, pointerEvents:'none' }}/>
        <div style={{ position:'absolute', width:90, height:90, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.06)', top:10, right:10, pointerEvents:'none' }}/>
      </>}

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.07em', textTransform:'uppercase', fontFamily:'DM Sans,sans-serif', color:hasReport?'rgba(255,255,255,0.45)':'var(--color-muted-foreground)' }}>Pinned Report</span>
          <span style={{ fontSize:9, padding:'2px 7px', borderRadius:20, fontFamily:'DM Sans,sans-serif', fontWeight:600, background:hasReport?'rgba(255,255,255,0.1)':'var(--color-border)', color:hasReport?'rgba(255,255,255,0.55)':'var(--color-muted-foreground)' }}>FIXED</span>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onConfigure(); }}
          className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition"
          style={{ background:hasReport?'rgba(255,255,255,0.1)':'var(--color-muted)', color:hasReport?'rgba(255,255,255,0.65)':'var(--color-muted-foreground)', borderColor:hasReport?'rgba(255,255,255,0.15)':'var(--color-border)' }}
        >
          {hasReport ? 'Change' : '+ Set Report'}
        </button>
      </div>

      {hasReport ? <>
        <p style={{ fontFamily:"'Space Grotesk', system-ui, sans-serif", fontWeight:600, fontSize:18, color:'#fff', lineHeight:1.25, margin:0 }}>{myReport.reportTitle || 'My Dashboard'}</p>
        <p style={{ fontSize:10, color:'rgba(255,255,255,0.38)', fontFamily:'DM Sans,sans-serif', marginTop:4 }}>{myReport.isSecure ? 'Secure report' : 'Public report'} · Click to open</p>
        {myReport.lastViewed && <p style={{ fontSize:9, color:'rgba(255,255,255,0.25)', fontFamily:'DM Sans,sans-serif', marginTop:2 }}>Last viewed: {myReport.lastViewed}</p>}
        <div style={{ position:'absolute', bottom:16, right:18, color:'rgba(255,255,255,0.18)', fontSize:18 }}>→</div>
      </> : <>
        <p className="text-sm text-muted-foreground mt-1">No report pinned yet</p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">Click "+ Set Report" to pin your key Power BI report to this page</p>
      </>}
    </div>
  );
};

// ─── REPORT PICKER MODAL ──────────────────────────────────────────────────────
export const ReportPickerModal = ({ reports, current, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const flattenLeafReports = (items) => {
    const out = [];
    (items || []).forEach(r => {
      if (r.children && r.children.length > 0) {
        out.push(...flattenLeafReports(r.children));
      } else if (r.type !== 'collapse') {
        out.push(r);
      }
    });
    return out;
  };
  const flatReports = flattenLeafReports(reports)
    .filter(r => r.title || r.name)
    .filter(r => (r.title || r.name || '').toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.65)' }} onClick={onClose}>
      <div className="surface-card rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 pb-3 border-b border-border/60">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-display text-base font-semibold" style={{ fontFamily:"'Space Grotesk', system-ui, sans-serif" }}>Pin a Report</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Always shown first on this page</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-sm">✕</button>
          </div>
          <input
            autoFocus
            className="w-full px-3 py-2 rounded-lg border border-border bg-muted/40 text-sm outline-none focus:border-primary transition-colors"
            placeholder="Search reports..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="overflow-y-auto p-4 flex flex-col gap-1.5">
          {flatReports.map((r, i) => {
            const id = r.reportId || r.id;
            const title = r.title || r.name;
            const isSel = current?.reportId === id || current?.id === id;
            return (
              <button key={id || i}
                onClick={() => { onSelect(r); onClose(); }}
                className={cn('flex items-center gap-3 p-3 rounded-xl border transition text-left', isSel ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-primary/40 hover:bg-primary/5')}
              >
                <div className={cn('h-9 w-9 rounded-lg grid place-items-center text-base flex-shrink-0', r.isSecure ? 'bg-amber-50' : 'bg-emerald-50')}>
                  {r.isSecure ? '🔒' : '📊'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{title}</p>
                  <p className="text-[11px] text-muted-foreground">{r.isSecure ? 'Secure · RLS' : 'Public'} · Power BI</p>
                </div>
                {isSel && <span className="text-primary font-bold text-sm flex-shrink-0">✓</span>}
              </button>
            );
          })}
          {flatReports.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No reports found</p>}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export function DepartmentCockpit({ section, pageTitle, pageEyebrow, pageDescription, children }) {
  const history  = useHistory();
  const dispatch = useDispatch();
  const account  = useSelector(s => s.account);
  const userAccountId = getUserAccountId(account || {});
  const isOwner = getUserRole(account || {}) === 'Owner';
  const orgId    = useSelector(s => s?.account?.org?.id || s?.account?.org?.data?.id || s?.account?.org_data?.id || s?.account?.org_data?.data?.id || s?.account?.org_member?.org_id || s?.account?.UserDetails?.org_id || s?.account?.user?.org_id);
  const userId   = useSelector(s => s?.account?.org_member?.id || s?.account?.UserDetails?.id || s?.account?.user?.id || s?.account?.user?.data?.id);

  const [cards,              setCards]              = useState([]);
  const [period,             setPeriod]             = useState('QTD');
  const [view,               setView]               = useState('cards');
  const [editMode,           setEditMode]           = useState(false);
  const [showPicker,         setShowPicker]         = useState(false);
  const [showReportPicker,   setShowReportPicker]   = useState(false);
  const [reports,            setReports]            = useState([]);
  const [kpiData,            setKpiData]            = useState({});
  // Derived KPI display metadata (name/category/icon/color/format/direction),
  // built from whatever kpi_master-backed metrics have come back from the API
  // so far. This never affects the underlying values, only how they're shown.
  const [kpiDefs,            setKpiDefs]            = useState({});
  const masterCardsRef = useRef([]);
  const reportsRef = useRef([]);  // always holds the latest reports — avoids stale closure in openReport
  const [myReport,           setMyReport]           = useState({ reportId: null, reportTitle: null, datasetId: null, groupId: null, pbReportId: null, isSecure: false, rlsRole: null, embedUrl: null, lastViewed: null });
  const [dragIdx,            setDragIdx]            = useState(null);
  const [dragOver,           setDragOver]           = useState(null);
  const [configLoaded,       setConfigLoaded]       = useState(false);

  // Keep a client-side guard as well as the backend filter. A KPI can never
  // leak into another department even if an older/cached API returns all rows.
  const sectionDepartment = normalizeDepartment(section);
  const deptKpis = Object.values(kpiDefs).filter(
    kpi => normalizeDepartment(kpi.category) === sectionDepartment
  );

  const deptReports = reports;

  const acctInitialized = account?.isInitialized;
  const acctOrgMemberId = account?.org_member?.id || null;

  useEffect(() => {
    if (!acctInitialized || !account?.isLoggedIn) return;

    const load = async () => {
      try {
        const apiBase = String(configData.API_SERVER || '').replace(/\/+$/, '');
        const getAllocated = async (query) => {
          try {
            const response = await axiosInstance.get(`${apiBase}/user/allocated-reports?${query}`);
            return response?.data || [];
          } catch (error) {
            if (error?.response?.status === 404) return [];
            throw error;
          }
        };

        const userReports = acctOrgMemberId
          ? await getAllocated(`org_member_id=${encodeURIComponent(acctOrgMemberId)}`)
          : [];
        const augmented = augmentReports(userReports);
        reportsRef.current = augmented;
        setReports(augmented);
      } catch (e) { console.error('DepartmentCockpit: failed to load reports', e); }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acctInitialized, acctOrgMemberId]);

  useEffect(() => {
    if (!orgId) {
      setKpiData({});
      return;
    }

    const controller = new AbortController();

    const loadKpis = async () => {
      let activityMetrics = {};
      let masterCards = [];

      // Single source of truth: /activity/dashboard_metrics/ already computes
      // every KPI value server-side (revenue, margins, headcount, etc). The
      // frontend only normalizes that response — it does NOT recalculate.
      try {
        const activityResponse = await axiosInstance.get(api('/activity/dashboard_metrics/'), {
          params: {
            ...(orgId ? { org_id: orgId } : {}),
            // org_id loads the department's full KPI Master catalogue. Do not
            // also send user_id: the older running backend interprets that as
            // a user_kpi_allocation filter and hides newly-created cards.
            ...(!orgId && !isOwner && userAccountId ? { user_id: userAccountId } : {}),
            period,
            section,
          },
          signal: controller.signal,
        });
        const activityPayload = activityResponse?.data || {};
        activityMetrics = buildActivityKpiData(activityPayload);
        const masterRows = activityPayload.kpi_master || activityPayload.kpiMaster || [];
        masterCards = cardsFromKpiMaster(masterRows);

        // KPI Master is the catalogue and must drive picker labels even when a
        // card has no Activity value yet. Formula code is only an internal rule.
        const masterDefinitionData = {};
        masterRows.forEach(row => {
          const code = row.kpi_code || row.kpiCode;
          if (!code) return;
          masterDefinitionData[String(code).toUpperCase()] = {
            value: null,
            prev: null,
            formula_code: row.formula_code || row.formulaCode || null,
            kpi_card_name: row.kpi_card_name || row.kpiCardName || null,
            department: row.department || null,
          };
        });
        activityMetrics = { ...masterDefinitionData, ...activityMetrics };
      } catch (activityError) {
        if (activityError.name !== 'CanceledError' && activityError.name !== 'AbortError') {
          console.error('DepartmentCockpit: failed to load activity KPI data', activityError);
        }
      }

      if (!controller.signal.aborted) {
        setKpiData(activityMetrics);
        setKpiDefs(buildKpiDefs(activityMetrics));
        masterCardsRef.current = masterCards;
        setCards((previousCards) => {
          const allowedCodes = new Set(masterCards.map(card => card.kpiCode));
          const retainedCards = previousCards
            .map(normalizeKpiCard)
            .filter(card => allowedCodes.has(card.kpiCode));
          return retainedCards.length ? retainedCards : masterCards;
        });
      }
    };

    loadKpis().catch((error) => {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('DepartmentCockpit: failed to load organization KPI data', error);
          setKpiData({});
        }
      });

    return () => controller.abort();
  }, [orgId, userAccountId, isOwner, period, section]);

  // Load saved cockpit config
  useEffect(() => {
    if (!orgId || !userId) return;
    setConfigLoaded(false);
    axiosInstance.get(`${configData.API_SERVER}user/cockpit-config/`, { params: { org_id: orgId, user_id: userId, section } })
      .then(res => {
        setMyReport(res.data.my_report || { reportId: null, reportTitle: null, datasetId: null, groupId: null, pbReportId: null, isSecure: false, rlsRole: null, embedUrl: null, lastViewed: null });
        if (res.data.kpi_cards?.length > 0) {
          const saved = res.data.kpi_cards.map(normalizeKpiCard).filter(card => card.kpiCode);
          const allowedCodes = new Set(masterCardsRef.current.map(card => card.kpiCode));
          const applicableSaved = allowedCodes.size ? saved.filter(card => allowedCodes.has(card.kpiCode)) : saved;
          setCards(applicableSaved.length ? applicableSaved : masterCardsRef.current);
        }
        if (res.data.period) setPeriod(res.data.period);
      }).catch(() => {}).finally(() => setConfigLoaded(true));
  }, [orgId, userId, section]);

  // Auto-save cockpit config (debounced 800ms)
  useEffect(() => {
    if (!orgId || !userId || !configLoaded) return;
    const t = setTimeout(() => {
      axiosInstance.post(`${configData.API_SERVER}user/cockpit-config/`, { org_id: orgId, user_id: userId, section, my_report: myReport, kpi_cards: cards, period }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [cards, myReport, period, section, orgId, userId, configLoaded]);

  function getCookie(n) {
    let v = null;
    if (document.cookie) document.cookie.split(';').forEach(c => { const t = c.trim(); if (t.startsWith(`${n}=`)) v = decodeURIComponent(t.slice(n.length + 1)); });
    return v;
  }

  const findReportInTree = (items, id) => {
    for (const r of (items || [])) {
      if (String(r.id || '') === String(id || '') || String(r.reportId || '') === String(id || '')) return r;
      const found = findReportInTree(r.children, id);
      if (found) return found;
    }
    return null;
  };

  const openReport = async (nodeId, reportTitle, datasetId, isSecure, rlsRole, embedUrl, groupId, pbId) => {
    const fullReport = findReportInTree(reportsRef.current, nodeId);
    if (!fullReport) {
      console.warn('Blocked report open: report is not allocated to the current user', nodeId);
      return;
    }
    const urlParsed = parsePowerBIUrl(embedUrl);

    const resolvedIsSecure = fullReport != null
      ? (fullReport.isSecure ?? false)
      : (isSecure === true || urlParsed.isSecure);

    console.log('[openReport]', { nodeId, fullReport, resolvedIsSecure, pbId, groupId });

    const allocatedUrl = fullReport?.embedURL || fullReport?.embedUrl || null;
    const allocatedParsed = parsePowerBIUrl(allocatedUrl);
    const resolvedDatasetId = fullReport?.datasetId  || datasetId || null;
    const resolvedGroupId   = fullReport?.groupId    || allocatedParsed.groupId || groupId || urlParsed.groupId || null;
    const resolvedRlsRole   = fullReport?.rlsRole    || rlsRole || null;
    const resolvedEmbedUrl  = allocatedUrl || embedUrl || null;
    const resolvedPbId      = fullReport?.pbReportId || allocatedParsed.pbReportId || pbId || urlParsed.pbReportId || null;

    try {
      if (resolvedIsSecure) {
        const csrf = getCookie('csrftoken');
        const tr = await axiosInstance.post(
          `${configData.API_SERVER}user/get-powerbi-embed-token`,
          { reportId: resolvedPbId, datasetId: resolvedDatasetId, orgId, rlsRole: resolvedRlsRole, groupId: resolvedGroupId },
          { headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf } }
        );
        const { embedToken, embedUrl: tokenEmbedUrl, groupId: tokenGroupId, datasetId: tokenDatasetId } = tr.data;

        const finalGroupId   = tokenGroupId   || resolvedGroupId;
        const finalDatasetId = tokenDatasetId || resolvedDatasetId;
        const sdkEmbedUrl    = tokenEmbedUrl
          || (resolvedPbId && finalGroupId
              ? `https://app.powerbi.com/reportEmbed?reportId=${resolvedPbId}&groupId=${finalGroupId}`
              : resolvedEmbedUrl);

        dispatch({
          type: SET_REPORT_DATA,
          payload: {
            title:       reportTitle,
            reportId:    resolvedPbId,
            isSecure:    true,
            embedUrl:    sdkEmbedUrl,
            reportData:  sdkEmbedUrl,
            accessToken: embedToken,
            datasetId:   finalDatasetId,
            groupId:     finalGroupId,
          },
        });
      } else {
        dispatch({
          type: SET_REPORT_DATA,
          payload: {
            title:      reportTitle,
            reportId:   resolvedPbId,
            isSecure:   false,
            embedUrl:   resolvedEmbedUrl,
            reportData: resolvedEmbedUrl,
            datasetId:  resolvedDatasetId,
            groupId:    resolvedGroupId,
          },
        });
      }
      history.push('/imagei/report');
    } catch (e) { console.error('openReport error:', e); }
  };

  const handlePinnedClick = async () => {
    if (!myReport.reportId) return;
    await openReport(myReport.reportId, myReport.reportTitle, myReport.datasetId, myReport.isSecure, myReport.rlsRole, myReport.embedUrl, myReport.groupId, myReport.pbReportId);
    setMyReport(prev => ({ ...prev, lastViewed: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }));
  };

  const handleCardClick = async (card) => {
    if (!card.reportId) return;
    await openReport(card.reportId, card.reportTitle, card.datasetId, card.isSecure, card.rlsRole, card.embedUrl, card.groupId, card.pbReportId);
  };

  const onDragStart = i => setDragIdx(i);
  const onDragOver  = (e, i) => { e.preventDefault(); setDragOver(i); };
  const onDrop      = () => {
    if (dragIdx === null || dragOver === null || dragIdx === dragOver) { setDragIdx(null); setDragOver(null); return; }
    const a = [...visibleCards]; const [m] = a.splice(dragIdx, 1); a.splice(dragOver, 0, m);
    setCards(a); setDragIdx(null); setDragOver(null);
  };

  // Never render a grid wrapper for a stale/unavailable saved KPI code.
  // An empty wrapper still occupies a CSS-grid cell and was creating the
  // apparently random gaps seen after KPI allocations changed.
  const visibleCards = cards
    .map(normalizeKpiCard)
    .filter(card => card.kpiCode && kpiDefs?.[card.kpiCode]);

  return (
    <>
      <PageHeader eyebrow={pageEyebrow} title={pageTitle} description={pageDescription} />

      <AICommentaryPanel scope={section || pageTitle} />

      <div className="px-4 sm:px-6 lg:px-10 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex w-full sm:w-auto bg-muted rounded-lg p-0.5 gap-0.5">
          {['MTD', 'QTD', 'YTD', 'FY'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              className={cn('flex-1 sm:flex-none px-3 py-1.5 rounded-md text-xs font-semibold transition-all', period === p ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {p}
            </button>
          ))}
        </div>
        <div className="flex w-full sm:w-auto items-center justify-end gap-2">
          <div className="flex bg-muted rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setView('cards')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all', view === 'cards' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
              title="Card view"
            >
              <LayoutGrid style={{ width: 13, height: 13 }} /> Cards
            </button>
            <button
              onClick={() => setView('table')}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all', view === 'table' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
              title="Table view"
            >
              <List style={{ width: 13, height: 13 }} /> Table
            </button>
          </div>
          {editMode && (
            <button onClick={() => setShowPicker(true)}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition">
              + Add KPI
            </button>
          )}
          <button onClick={() => setEditMode(e => !e)}
            className={cn('px-3 py-1.5 rounded-lg border text-xs font-semibold transition', editMode ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:text-foreground')}>
            {editMode ? 'Done' : 'Customise'}
          </button>
        </div>
      </div>

      <section className="px-4 sm:px-6 lg:px-10 pb-10">
        {view === 'table' ? (
          <KPITable
            cards={cards}
            period={period}
            kpiData={kpiData}
            kpiDefs={kpiDefs}
            onCardClick={handleCardClick}
          />
        ) : (
          <>
        {editMode && (
          <p className="text-[11px] text-muted-foreground italic mb-3">Drag ⠇ to reorder · ✕ to remove · click a card to link a report</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">

          {visibleCards.map((card, idx) => {
            return (
            <div key={card.kpiCode}
              draggable={editMode}
              onDragStart={() => onDragStart(idx)}
              onDragOver={e => onDragOver(e, idx)}
              onDrop={onDrop}
              onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
              style={{ opacity: dragIdx === idx ? 0.4 : 1, outline: dragOver === idx && dragIdx !== idx ? '2px dashed var(--color-primary, #3b82f6)' : 'none', borderRadius: 'var(--radius, 0.75rem)', transition: 'opacity 0.15s' }}
            >
              <KPICard
                card={card}
                period={period}
                kpiData={kpiData}
                kpiDefs={kpiDefs}
                onClick={() => handleCardClick(card)}
                onRemove={() => setCards(p => p.map(normalizeKpiCard).filter(c => c.kpiCode !== card.kpiCode))}
                editMode={editMode}
                isDragging={dragIdx === idx}
              />
            </div>
          );})}

          {editMode && (
            <button onClick={() => setShowPicker(true)}
              className="surface-card border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition flex flex-col items-center justify-center gap-1.5 min-h-[148px] text-muted-foreground hover:text-primary">
              <span className="text-2xl font-light">+</span>
              <span className="text-[11px] font-semibold">Add KPI</span>
            </button>
          )}
        </div>
          </>
        )}
      </section>

      {children && (
        <div className="px-4 sm:px-6 lg:px-10 pb-10">
          {typeof children === 'function' ? children({ period }) : children}
        </div>
      )}

      {showPicker && (
        <AddKPIModal
          activeKpiCodes={visibleCards.map(c => c.kpiCode)}
          availableKpis={deptKpis}
          reports={deptReports}
          onAdd={card => setCards(p => [...p, card])}
          onClose={() => setShowPicker(false)}
        />
      )}

    </>
  );
}
